import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { Link } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { CheckCircle2, XCircle, Loader2, Clock, Minus, ArrowLeft, AlertTriangle, AlertCircle, Trash2, RefreshCw, ArrowUpDown, Ban, RotateCcw } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useStaleEnrichmentCleanup } from '@/hooks/useStaleEnrichmentCleanup';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

import { toast } from 'sonner';
import { enrichLeadWithWebSearch } from '@/services/webSearchService';

interface LeadEnrichmentRow {
  id: string;
  name: string;
  address: string | null;
  website: string | null;
  category: string | null;
  web_search_status: string | null;
  deep_research_status: string | null;
  web_search_data: any | null;
  updated_at: string;
}

interface JobError {
  entity_id: string;
  job_type: string;
  error_message: string | null;
  status: string;
  updated_at: string;
}

interface ActiveJob {
  entity_id: string;
  job_type: string;
  current_page: string | null;
  pages_scraped: number;
  total_pages: number;
  logs: any[] | null;
  status: string;
  updated_at: string;
}

type StageStatus = 'completed' | 'processing' | 'failed' | 'pending' | 'skipped' | null;

function stageIcon(status: StageStatus): { icon: React.ReactNode; label: string } {
  switch (status) {
    case 'completed':
      return { icon: <CheckCircle2 className="h-4 w-4 text-emerald-500" />, label: 'Completed' };
    case 'processing':
      return { icon: <Loader2 className="h-4 w-4 text-amber-500 animate-spin" />, label: 'Processing' };
    case 'failed':
      return { icon: <XCircle className="h-4 w-4 text-destructive" />, label: 'Failed' };
    case 'pending':
      return { icon: <Clock className="h-4 w-4 text-muted-foreground" />, label: 'Queued — waiting to start' };
    case 'skipped':
      return { icon: <AlertCircle className="h-4 w-4 text-amber-500" />, label: 'Skipped' };
    default:
      return { icon: <Minus className="h-4 w-4 text-muted-foreground/40" />, label: 'Not started' };
  }
}

function normalizeStatus(s: string | null): StageStatus {
  if (!s) return null;
  if (s === 'completed') return 'completed';
  if (['processing', 'pending', 'retrying', 'scraping', 'extracting'].includes(s)) return 'processing';
  if (s === 'skipped') return 'skipped' as StageStatus;
  if (s === 'failed') return 'failed';
  return null;
}

function getFirecrawlStatus(lead: LeadEnrichmentRow): StageStatus {
  const wsd = lead.web_search_data;
  if (wsd?.firecrawlSearchTimestamp) return 'completed';
  if (wsd?.enrichmentStage === 'firecrawl_search') return 'processing';
  const ws = normalizeStatus(lead.web_search_status);
  if (ws === 'completed' && !wsd?.firecrawlSearchTimestamp) return 'failed';
  if (ws === 'processing') return 'pending';
  return null;
}

function getLeadCategory(lead: LeadEnrichmentRow, activeJobsForLead?: ActiveJob[]): 'processing' | 'failed' | 'done' | 'queued' | 'skipped' {
  const ws = normalizeStatus(lead.web_search_status);
  const dr = normalizeStatus(lead.deep_research_status);
  const fc = getFirecrawlStatus(lead);

  // Terminal states: completed or skipped (hit retry cap)
  const wsTerminal = ws === 'completed' || ws === 'skipped';
  const drTerminal = dr === 'completed' || dr === 'skipped';

  // Any stage skipped = skipped category (for review)
  if ((ws === 'skipped' || dr === 'skipped') && wsTerminal && drTerminal) return 'skipped';

  // All stages fully completed → done
  if (wsTerminal && fc === 'completed' && drTerminal) return 'done';
  if (wsTerminal && drTerminal) return 'done';

  // Terminal lead failures take precedence over stale jobs
  if (ws === 'failed' || dr === 'failed' || fc === 'failed') return 'failed';

  // Only treat jobs as "active" if they are recent (< 15 min old)
  if (activeJobsForLead && activeJobsForLead.length > 0) {
    const fifteenMinAgo = Date.now() - 15 * 60 * 1000;
    const hasFreshJob = activeJobsForLead.some(j => {
      const jobUpdated = (j as any).updated_at ? new Date((j as any).updated_at).getTime() : 0;
      return jobUpdated > fifteenMinAgo;
    });
    if (hasFreshJob) return 'processing';
  }

  if (ws === 'processing' || dr === 'processing' || fc === 'processing') return 'processing';
  return 'queued';
}

function getProgressText(job: ActiveJob | undefined): string | null {
  if (!job) return null;
  
  // For deep research jobs, show page progress
  if (job.job_type === 'deep_research' || job.job_type === 'firecrawl-crawl-lead') {
    if (job.current_page) {
      const parts = [];
      if (job.pages_scraped > 0 || job.total_pages > 0) {
        parts.push(`Page ${job.pages_scraped}/${job.total_pages}`);
      }
      parts.push(job.current_page);
      return parts.join(' — ');
    }
    if (job.pages_scraped > 0) return `Scraping page ${job.pages_scraped}/${job.total_pages}`;
  }
  
  // For web_search jobs, show last log step
  if (job.job_type === 'web_search' && job.logs && Array.isArray(job.logs)) {
    const lastLog = job.logs[job.logs.length - 1];
    if (lastLog?.step) {
      const stepLabels: Record<string, string> = {
        'job_created': 'Queued',
        'worker_started': 'Starting search...',
        'step1_complete': 'Web search done',
        'firecrawl_search_start': 'Starting Firecrawl...',
        'firecrawl_queries_start': `Running ${lastLog.queryCount || ''} search queries...`,
        'firecrawl_queries_complete': `Scraped ${lastLog.pagesScraped || 0} pages`,
        'firecrawl_ai_extraction_start': 'AI extracting contacts...',
        'firecrawl_ai_extraction_complete': `Found ${lastLog.peopleFound || 0} people, ${lastLog.companyEmailsFound || 0} emails`,
        'firecrawl_search_complete': 'Firecrawl done',
        'firecrawl_no_content': 'No content found',
        'create_contacts_start': 'Creating contacts...',
        'create_contacts_complete': `Created contacts (${lastLog.totalPeople || 0} total)`,
        'step2_complete': 'All steps complete',
        'deep_research_triggered': 'Deep research triggered',
      };
      return stepLabels[lastLog.step] || lastLog.step;
    }
  }
  
  return job.current_page || null;
}

export default function EnrichmentQueue() {
  const { activeCompany, user } = useAuth();
  useStaleEnrichmentCleanup(activeCompany?.id, user?.id);
  const [leads, setLeads] = useState<LeadEnrichmentRow[]>([]);
  const [jobErrors, setJobErrors] = useState<Map<string, JobError[]>>(new Map());
  const [activeJobs, setActiveJobs] = useState<Map<string, ActiveJob[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [retryingIds, setRetryingIds] = useState<Set<string>>(new Set());
  const [bulkCancelling, setBulkCancelling] = useState(false);
  const [bulkRequeuing, setBulkRequeuing] = useState(false);
  const [sortField, setSortField] = useState<'updated_at' | 'name'>('updated_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleRetryEnrichment = useCallback(async (lead: LeadEnrichmentRow) => {
    setRetryingIds(prev => new Set(prev).add(lead.id));
    try {
      const jobId = await enrichLeadWithWebSearch({
        id: lead.id,
        name: lead.name,
        address: lead.address || undefined,
        website: lead.website || undefined,
      } as any);
      if (jobId) {
        toast.success(`Enrichment retry started for "${lead.name}"`);
      } else {
        toast.error('Failed to retry enrichment');
      }
    } catch {
      toast.error('Failed to retry enrichment');
      setRetryingIds(prev => {
        const next = new Set(prev);
        next.delete(lead.id);
        return next;
      });
    }
  }, []);

  const handleRemoveFromQueue = useCallback(async (leadId: string, leadName: string) => {
    setDeletingIds(prev => new Set(prev).add(leadId));
    try {
      // Cancel active enrichment jobs first
      const { error: jobErr } = await supabase
        .from('enrichment_jobs')
        .update({ status: 'failed', error_message: 'Cancelled by user' } as any)
        .eq('entity_id', leadId)
        .in('status', ['pending', 'processing']);

      if (jobErr) {
        console.error('Error cancelling jobs:', jobErr);
      }

      // Set both statuses to 'skipped' so auto-recovery ignores this lead
      const { error: leadErr } = await supabase
        .from('leads')
        .update({ 
          web_search_status: 'skipped', 
          deep_research_status: 'skipped',
          updated_at: new Date().toISOString(),
        })
        .eq('id', leadId);

      if (leadErr) {
        console.error('Error updating lead status to skipped:', leadErr);
        toast.error(`Failed to remove from queue: ${leadErr.message}`);
        return;
      }

      setLeads(prev => prev.filter(l => l.id !== leadId));
      toast.success(`Removed "${leadName}" from queue`);
    } catch (err) {
      console.error('Error removing lead from queue:', err);
      toast.error('Failed to remove from queue');
    } finally {
      setDeletingIds(prev => {
        const next = new Set(prev);
        next.delete(leadId);
        return next;
      });
    }
  }, []);
  const fetchData = useCallback(async () => {
    if (!activeCompany?.id || !user?.id) return;

    // Paginated fetch to bypass Supabase 1000-row default limit
    let allLeads: any[] = [];
    let page = 0;
    const pageSize = 1000;
    let hasMore = true;

    while (hasMore) {
      const from = page * pageSize;
      const to = from + pageSize - 1;
      const { data, error } = await supabase
        .from('leads')
        .select('id, name, address, website, category, web_search_status, deep_research_status, web_search_data, updated_at')
        .eq('company_id', activeCompany.id)
        .eq('created_by', user.id)
        .order('updated_at', { ascending: false })
        .range(from, to);

      if (error) {
        console.error('Error fetching leads page:', error);
        hasMore = false;
      } else if (data && data.length > 0) {
        allLeads = [...allLeads, ...data];
        page++;
        hasMore = data.length === pageSize;
      } else {
        hasMore = false;
      }
    }

    const leadsData = allLeads;
    const leadIds = leadsData.map(l => l.id);

    // Chunk lead IDs to avoid PostgREST URL length limits
    const chunkSize = 200;
    const chunks: string[][] = [];
    for (let i = 0; i < leadIds.length; i += chunkSize) {
      chunks.push(leadIds.slice(i, i + chunkSize));
    }
    const safeChunks = chunks.length > 0 ? chunks : [['00000000-0000-0000-0000-000000000000']];

    const [failedResults, activeResults] = await Promise.all([
      Promise.all(safeChunks.map(chunk =>
        supabase
          .from('enrichment_jobs')
          .select('entity_id, job_type, error_message, status, updated_at')
          .eq('status', 'failed')
          .in('entity_id', chunk)
          .order('updated_at', { ascending: false })
          .limit(1000)
      )),
      Promise.all(safeChunks.map(chunk =>
        supabase
          .from('enrichment_jobs')
          .select('entity_id, job_type, current_page, pages_scraped, total_pages, logs, status, updated_at')
          .in('status', ['pending', 'processing'])
          .in('entity_id', chunk)
          .order('updated_at', { ascending: false })
          .limit(500)
      )),
    ]);

    const jobsRes = { data: failedResults.flatMap(r => r.data || []), error: failedResults.find(r => r.error)?.error || null };
    const activeJobsRes = { data: activeResults.flatMap(r => r.data || []), error: activeResults.find(r => r.error)?.error || null };

    if (leadsData.length > 0) {
      setLeads(leadsData);
      setRetryingIds(prev => {
        if (prev.size === 0) return prev;
        const next = new Set(prev);
        for (const id of prev) {
          const lead = leadsData.find(l => l.id === id);
          if (lead) {
            const cat = getLeadCategory(lead, activeJobsRes.data?.filter(j => j.entity_id === id) as ActiveJob[]);
            if (cat !== 'processing') next.delete(id);
          }
        }
        return next.size === prev.size ? prev : next;
      });
    }

    if (jobsRes.data) {
      const map = new Map<string, JobError[]>();
      for (const job of jobsRes.data) {
        const existing = map.get(job.entity_id) || [];
        if (!existing.find(e => e.job_type === job.job_type)) {
          existing.push(job);
          map.set(job.entity_id, existing);
        }
      }
      setJobErrors(map);
    }

    if (activeJobsRes.data) {
      const map = new Map<string, ActiveJob[]>();
      for (const job of activeJobsRes.data) {
        const existing = map.get(job.entity_id) || [];
        existing.push(job as ActiveJob);
        map.set(job.entity_id, existing);
      }
      setActiveJobs(map);
    }

    setLoading(false);
  }, [activeCompany?.id, user?.id]);

  const handleBulkCancel = useCallback(async () => {
    if (!activeCompany?.id || !user?.id) return;
    setBulkCancelling(true);
    try {
      const queuedLeadIds = leads
        .filter(l => {
          const cat = getLeadCategory(l, activeJobs.get(l.id));
          return cat === 'queued' || cat === 'processing' || cat === 'failed';
        })
        .map(l => l.id);

      if (queuedLeadIds.length === 0) {
        toast.info('No leads to cancel');
        return;
      }

      await supabase
        .from('enrichment_jobs')
        .update({ status: 'failed', error_message: 'Bulk cancelled by user' } as any)
        .in('entity_id', queuedLeadIds)
        .in('status', ['pending', 'processing']);

      await supabase
        .from('leads')
        .update({
          web_search_status: 'skipped',
          deep_research_status: 'skipped',
          updated_at: new Date().toISOString(),
        })
        .in('id', queuedLeadIds);

      toast.success(`Cancelled enrichment for ${queuedLeadIds.length} leads`);
      await fetchData();
    } catch (err) {
      console.error('Bulk cancel error:', err);
      toast.error('Failed to cancel enrichment');
    } finally {
      setBulkCancelling(false);
    }
  }, [activeCompany?.id, user?.id, leads, activeJobs, fetchData]);

  const handleBulkRequeue = useCallback(async () => {
    if (!activeCompany?.id || !user?.id) return;
    setBulkRequeuing(true);
    try {
      const skippedLeadIds = leads
        .filter(l => getLeadCategory(l, activeJobs.get(l.id)) === 'skipped')
        .map(l => l.id);

      if (skippedLeadIds.length === 0) {
        toast.info('No skipped leads to re-queue');
        return;
      }

      await supabase
        .from('leads')
        .update({
          web_search_status: null,
          deep_research_status: null,
          updated_at: new Date().toISOString(),
        } as any)
        .in('id', skippedLeadIds);

      toast.success(`Re-queued ${skippedLeadIds.length} leads for enrichment`);
      await fetchData();
    } catch (err) {
      console.error('Bulk re-queue error:', err);
      toast.error('Failed to re-queue leads');
    } finally {
      setBulkRequeuing(false);
    }
  }, [activeCompany?.id, user?.id, leads, activeJobs, fetchData]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000); // 15s for better progress visibility
    return () => clearInterval(interval);
  }, [fetchData]);

  const counts = useMemo(() => {
    let processing = 0, failed = 0, done = 0, queued = 0, skipped = 0;
    leads.forEach(l => {
      const cat = getLeadCategory(l, activeJobs.get(l.id));
      if (cat === 'processing') processing++;
      else if (cat === 'failed') failed++;
      else if (cat === 'done') done++;
      else if (cat === 'skipped') skipped++;
      else queued++;
    });
    return { processing, failed, done, queued, skipped, total: leads.length };
  }, [leads, activeJobs]);

  const filteredLeads = useMemo(() => {
    const base = tab === 'all' ? leads : leads.filter(l => getLeadCategory(l, activeJobs.get(l.id)) === tab);
    return [...base].sort((a, b) => {
      if (sortField === 'name') {
        return sortDir === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      }
      const dateA = new Date(a.updated_at).getTime();
      const dateB = new Date(b.updated_at).getTime();
      return sortDir === 'asc' ? dateA - dateB : dateB - dateA;
    });
  }, [leads, tab, sortField, sortDir, activeJobs]);

  const toggleSort = useCallback((field: 'updated_at' | 'name') => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'name' ? 'asc' : 'desc');
    }
  }, [sortField]);

  const eta = useMemo(() => {
    let remainingSteps = 0;
    leads.forEach(l => {
      if (normalizeStatus(l.web_search_status) !== 'completed') remainingSteps++;
      if (normalizeStatus(l.deep_research_status) !== 'completed') remainingSteps++;
    });
    const totalMinutes = Math.ceil((remainingSteps / 2) * 2);
    if (totalMinutes <= 0) return null;
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return hours > 0 ? `~${hours}h ${mins}m` : `~${mins}m`;
  }, [leads]);

  function getErrorForLead(leadId: string): string | null {
    const errors = jobErrors.get(leadId);
    if (!errors || errors.length === 0) return null;
    const msgs = errors
      .filter(e => e.error_message && e.job_type !== 'firecrawl-crawl-lead')
      .map(e => `${e.job_type}: ${e.error_message}`);
    return msgs.length > 0 ? msgs.join(' | ') : null;
  }

  function getActiveJobForLead(leadId: string, jobType: string): ActiveJob | undefined {
    return activeJobs.get(leadId)?.find(j => j.job_type === jobType);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/app" className="text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <h1 className="text-2xl font-bold text-foreground">Enrichment Queue</h1>
          </div>
          {eta && (
            <Badge variant="outline" className="text-sm px-3 py-1">
              <Clock className="h-3.5 w-3.5 mr-1.5" />
              Est: {eta} remaining
            </Badge>
          )}
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <SummaryCard label="Complete" count={counts.done} icon={<CheckCircle2 className="h-5 w-5 text-emerald-500" />} />
          <SummaryCard label="Processing" count={counts.processing} icon={<Loader2 className="h-5 w-5 text-amber-500 animate-spin" />} />
          <SummaryCard label="Failed" count={counts.failed} icon={<XCircle className="h-5 w-5 text-destructive" />} />
          <SummaryCard label="Queued" count={counts.queued} icon={<Clock className="h-5 w-5 text-muted-foreground" />} />
          <SummaryCard label="Skipped" count={counts.skipped} icon={<AlertTriangle className="h-5 w-5 text-amber-500" />} />
        </div>

        {/* Bulk actions */}
        {(counts.queued + counts.processing + counts.failed > 0 || counts.skipped > 0) && (
          <div className="flex items-center gap-3">
            {counts.queued + counts.processing + counts.failed > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    disabled={bulkCancelling}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-destructive/30 text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                  >
                    {bulkCancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
                    Cancel All Queued ({counts.queued + counts.processing + counts.failed})
                  </button>
                </AlertDialogTrigger>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center text-muted-foreground cursor-help">
                      <AlertCircle className="h-3.5 w-3.5" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[280px] text-xs">
                    Cancels all leads that are queued, processing, or failed. Active jobs are stopped and leads are marked as "skipped". No leads are deleted.
                  </TooltipContent>
                </Tooltip>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel all queued enrichment?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will stop enrichment for <strong>{counts.queued + counts.processing + counts.failed}</strong> leads and mark them as skipped. You can re-queue them later when rate limits clear.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep Running</AlertDialogCancel>
                    <AlertDialogAction onClick={handleBulkCancel} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      Cancel All
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {counts.skipped > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    disabled={bulkRequeuing}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-border text-foreground hover:bg-accent transition-colors disabled:opacity-50"
                  >
                    {bulkRequeuing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                    Re-queue All Skipped ({counts.skipped})
                  </button>
                </AlertDialogTrigger>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center text-muted-foreground cursor-help">
                      <AlertCircle className="h-3.5 w-3.5" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[280px] text-xs">
                    Resets all skipped leads back to the queue (status → null) so the enrichment worker picks them up automatically in the next cycle.
                  </TooltipContent>
                </Tooltip>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Re-queue all skipped leads?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will reset <strong>{counts.skipped}</strong> skipped leads back to the queue. The enrichment pipeline will pick them up automatically in controlled batches.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleBulkRequeue}>
                      Re-queue All
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        )}

        {/* Tabs + Table */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="all">All ({counts.total})</TabsTrigger>
            <TabsTrigger value="processing">Processing ({counts.processing})</TabsTrigger>
            <TabsTrigger value="failed">Failed ({counts.failed})</TabsTrigger>
            <TabsTrigger value="queued">Queued ({counts.queued})</TabsTrigger>
            <TabsTrigger value="done">Done ({counts.done})</TabsTrigger>
            <TabsTrigger value="skipped">Skipped ({counts.skipped})</TabsTrigger>
          </TabsList>

          <TabsContent value={tab}>
            <ScrollArea className="h-[calc(100vh-340px)]">
              <Table>
                <TableHeader>
                  <TableRow>
                     <TableHead className="min-w-[200px]">
                       <button onClick={() => toggleSort('name')} className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                         Lead <ArrowUpDown className={cn("h-3.5 w-3.5", sortField === 'name' ? 'text-foreground' : 'text-muted-foreground/50')} />
                       </button>
                     </TableHead>
                     <TableHead className="text-center w-[100px]">Web Search</TableHead>
                     <TableHead className="text-center w-[120px]">Firecrawl</TableHead>
                     <TableHead className="text-center w-[120px]">Deep Research</TableHead>
                     <TableHead className="w-[120px]">
                       <button onClick={() => toggleSort('updated_at')} className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                         Run Date <ArrowUpDown className={cn("h-3.5 w-3.5", sortField === 'updated_at' ? 'text-foreground' : 'text-muted-foreground/50')} />
                       </button>
                     </TableHead>
                     <TableHead className="min-w-[200px]">Error</TableHead>
                     <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-12">
                        No leads in this category
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLeads.map(lead => {
                      const cat = getLeadCategory(lead, activeJobs.get(lead.id));
                      const error = cat === 'done' ? null : getErrorForLead(lead.id);
                      const wsJob = getActiveJobForLead(lead.id, 'web_search');
                      const drJob = getActiveJobForLead(lead.id, 'deep_research');
                      const wsProgress = getProgressText(wsJob);
                      const drProgress = getProgressText(drJob);
                      const fcStatus = getFirecrawlStatus(lead);
                      const wsStatus = normalizeStatus(lead.web_search_status);
                      const drStatus = normalizeStatus(lead.deep_research_status);

                      return (
                        <TableRow key={lead.id} className={cn(cat === 'failed' && 'bg-destructive/5')}>
                          <TableCell>
                            <Link
                              to={`/leads/${lead.id}`}
                              className="font-medium text-foreground hover:text-primary transition-colors hover:underline"
                            >
                              {lead.name}
                            </Link>
                          </TableCell>
                          <TableCell className="text-center">
                            <StageWithProgress status={wsStatus} progress={wsProgress} />
                          </TableCell>
                          <TableCell className="text-center">
                            <StageWithProgress status={fcStatus} progress={fcStatus === 'processing' ? wsProgress : null} />
                          </TableCell>
                          <TableCell className="text-center">
                            <StageWithProgress status={drStatus} progress={drProgress} />
                          </TableCell>
                          <TableCell>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-xs text-muted-foreground cursor-help whitespace-nowrap">
                                  {formatDistanceToNow(new Date(lead.updated_at), { addSuffix: true })}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-xs">
                                {format(new Date(lead.updated_at), 'PPpp')}
                              </TooltipContent>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            {error && (
                              <div className="flex items-start gap-1.5 text-xs text-destructive max-w-[300px]">
                                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                                <span className="line-clamp-2">{error}</span>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {(cat === 'failed' || retryingIds.has(lead.id)) && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => handleRetryEnrichment(lead)}
                                      disabled={retryingIds.has(lead.id)}
                                      className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
                                    >
                                      {retryingIds.has(lead.id) ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <RefreshCw className="h-4 w-4" />
                                      )}
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="text-xs">
                                    Retry the full enrichment pipeline for this lead
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <button
                                    disabled={deletingIds.has(lead.id)}
                                    className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                                  >
                                    {deletingIds.has(lead.id) ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-4 w-4" />
                                    )}
                                  </button>
                                </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Remove from queue?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will stop any active enrichment for <strong>{lead.name}</strong> and remove it from the queue. The lead itself will not be deleted.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleRemoveFromQueue(lead.id, lead.name)}>
                                    Remove
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </div>
    </TooltipProvider>
  );
}

function StageWithProgress({ status, progress }: { status: StageStatus; progress: string | null }) {
  const { icon, label } = stageIcon(status);
  const tooltipText = (status === 'processing' && progress) ? progress : label;
  
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-help">{icon}</span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[250px] text-xs">
        {tooltipText}
      </TooltipContent>
    </Tooltip>
  );
}

function SummaryCard({ label, count, icon }: { label: string; count: number; icon: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-3">
      {icon}
      <div>
        <p className="text-2xl font-bold text-foreground">{count}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}
